import React from 'react'
import PropTypes from 'prop-types'
import { Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import BatchReasonsList, { translateBatchReasons, translateBatchRejectionMessage } from './batchReasons'

/**
 * Historia 120 — Bloque 11 (Jupiter, paquete P5), S127:
 * `features/S116-previsualizacion-y-modo-jupiter.feature`.
 *
 * Renders N2's response (`batchApi.js`'s `startBatch`) job by job: what started, and what was left
 * out with its translated reason -- the FOURTH surface of the one reason renderer/translator rule
 * (§7.2 #15-bis, `batchReasons.jsx`). Never framed as a gesture failure (RN19/E11) and never offers
 * an undo (there is no "undo a batch start", CU4/A6) -- this is a plain acknowledgement, dismissible
 * with the one generic `Close` action, not a decision.
 *
 * ⚠️ `result` is taken and passed around WHOLE, never destructured down to `{started, notStarted}` by
 * a caller: `warnings[]` (E-B7, `BatchRecordNotPersisted` when `batchId` is null but two or more jobs
 * DID start, D-SP19) is the SIXTH surface of the same translator (`translateBatchReasons`, the exact
 * function `reasons[]` uses) and must survive to here. This component does not read or store
 * `batchId` at all -- deciding whether to offer batch actions from it is a LATER package's job
 * (P6/P7), never this one's, and never gated on `started.length`.
 *
 * ⚠️ Historia 120 (Jupiter, paquete P10), S210/D-SP20/§7.2 #15-ter: N2's gesture-wide `400`
 * (`BatchSelectionEmpty`/`BatchDuplicateJob`/`BatchInvalidTargetStatus`/`JobNotInDeviceQueue`/
 * `JobGovernedByAcceptance`/the external-circuit literal) is a DIFFERENT shape from a `200 SUCCESS`
 * (nothing started, nothing left out -- the whole gesture never happened), so the caller passes it
 * as `rejection` (the raw error `Api_Functions.jsx` already resolved to `{message}`) instead of
 * `result`. This is the FIFTH surface of the one translator (`translateBatchRejectionMessage`,
 * `batchReasons.jsx`, P3) -- the SAME function `notStarted[].reasons` and `warnings[]` use, no
 * whitelist, no second renderer per transport (that is exactly the defect B3 flagged). `rejection`
 * and `result` are mutually exclusive: when a rejection is present nothing else in this dialog
 * renders, because best-effort NEVER applies to a gesture-wide `400` (RN18/POL4 -- N2 either runs the
 * confirmed list or runs nothing at all for it).
 *
 * N2 does not carry the display fields (`fabricationOrderCode`/`partReferenceCode`/`nodeName`) on its
 * `started`/`notStarted` rows (only the id pair) -- `jobLabels` is the lookup the caller builds from
 * the preview response it already fetched (`BatchStartPreviewDialog`'s `previewJobs`), keyed the same
 * way (`fabricationOrderId:jobReferenceId`), so this dialog never needs a second N1-shaped call.
 */
const rowKey = (job) => `${job?.fabricationOrderId}:${job?.jobReferenceId}`

const BatchStartResultDialog = ({ open, onClose, result, jobLabels = {}, rejection = null }) => {
  const { t_ } = useSmartTranslation()
  const started = Array.isArray(result?.started) ? result.started : []
  const notStarted = Array.isArray(result?.notStarted) ? result.notStarted : []
  const warnings = Array.isArray(result?.warnings) ? result.warnings : []

  const labelFor = (job) => {
    const label = jobLabels[rowKey(job)]
    if (!label) return `${job?.fabricationOrderId} — ${job?.jobReferenceId}`
    return [label.fabricationOrderCode, label.partReferenceCode, label.nodeName].filter(Boolean).join(' — ')
  }

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Start batch'), icon: CheckCircleOutlineIcon })}
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
    >
      <Stack spacing={3} sx={{ textAlign: 'left' }}>
        {rejection
          ? (
            <Typography color='error'>{translateBatchRejectionMessage(t_, rejection)}</Typography>
            )
          : (
            <>
              {warnings.length > 0 && (
                <Stack spacing={0.5}>
                  {translateBatchReasons(t_, warnings).map((warning, index) => (
                    <Typography key={`${index}-${warning}`} color='warning.main'>{warning}</Typography>
                  ))}
                </Stack>
              )}

              {started.length > 0 && (
                <Stack spacing={1}>
                  {started.map((job) => (
                    <Stack key={rowKey(job)} direction='row' alignItems='center' spacing={1}>
                      <CheckCircleOutlineIcon color='success' fontSize='small' />
                      <Typography>{labelFor(job)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              {notStarted.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant='subtitle1' fontWeight='bold'>{t_('Jobs left out')}</Typography>
                  {notStarted.map((job) => (
                    <Stack key={rowKey(job)} spacing={0.5}>
                      <Typography>{labelFor(job)}</Typography>
                      <BatchReasonsList reasons={job.reasons} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </>
            )}
      </Stack>
    </MosSmartDialog>
  )
}

BatchStartResultDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  result: PropTypes.shape({
    type: PropTypes.string,
    batchId: PropTypes.string,
    started: PropTypes.arrayOf(PropTypes.shape({
      fabricationOrderId: PropTypes.number,
      jobReferenceId: PropTypes.number
    })),
    notStarted: PropTypes.arrayOf(PropTypes.shape({
      fabricationOrderId: PropTypes.number,
      jobReferenceId: PropTypes.number,
      reasons: PropTypes.arrayOf(PropTypes.string)
    })),
    warnings: PropTypes.arrayOf(PropTypes.string)
  }),
  jobLabels: PropTypes.object,
  rejection: PropTypes.shape({
    message: PropTypes.string
  })
}

export default BatchStartResultDialog
